import Avatar from 'further-ui/components/Avatar';
import Button from 'components/Button';
import { Column, Row } from 'further-ui/components/Layout';
import ProcessingPage from 'components/new/ProcessingPage';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { revaluation as rules } from 'further-ui/validations';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { Divider, Typography } from '@mui/material';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { isEmpty, sumBy, values } from 'lodash';
import React, { useState } from 'react';
import processRevaluationHistory from '../../../helpers/processRevaluationHistory';
import CompanyReviewSection from './CompanyReviewSection';
import CompanyRevaluationHistorySection from './CompanyRevaluationHistorySection';
import filterChangedRevaluations from '../../helpers/filterChangedRevaluations';
import StaticField from 'further-ui/components/StaticField';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { captureException } from '@sentry/react';
import FullPageDrawer from 'components/new/FullPageDrawer';
import BulkRevaluationsDrawerTitle from '../BulkRevaluationsDrawerTitle';

type CollectedDataEntry = {
  formData: rules.RevaluationFormType;
  assetId: string;
  assetName: string;
  name: string;
  companyLogo?: string | null;
};

type Props = {
  collectedData: Record<string, CollectedDataEntry>;
  vehicle: Vehicle;
  periodId?: string;
  onBack: () => void;
  onExit: () => void;
};

type CompanyRow = {
  id: string;
  name: string;
  assetName: string;
  assetId: string;
  formData: rules.RevaluationFormType;
  companyLogo?: string | null;
  assetCurrency: CurrencyCode;
  hasChanges: boolean;
  priorHoldValueTotal: number;
  newHoldValueTotal: number;
  priorUnitPriceTotal: number;
  newUnitPriceTotal: number;
  valuationChangeTotal: number;
};

function buildItemPayload(
  entry: CollectedDataEntry,
  baseCurrency: CurrencyCode,
) {
  const { formData, assetId } = entry;
  const allRevaluations = [
    ...(formData.revaluations ?? []),
    ...(formData.newRevaluations ?? []).filter((r) => !r.isRemoved),
  ];
  const revaluationHistory = processRevaluationHistory(allRevaluations);
  const latestRevaluations = filterChangedRevaluations(
    revaluationHistory,
    baseCurrency,
    formData.revaluationCurrencyRates,
  );

  const globalFxRate = formData.revaluationCurrencyRates?.global ?? null;

  return {
    revaluations: latestRevaluations.map((rev) => ({
      ...rev,
      ledgerDate: new CalendarDay(rev.date),
      unitPriceTcy: rev.newUnitPrice,
      assetId,
    })),
    revaluationTransactions: formData.revaluationTransactions,
    revaluationCurrencyRates: Object.fromEntries(
      latestRevaluations.map((rev) => [
        rev.holdingClassId,
        formData.revaluationCurrencyRates?.[rev.holdingClassId] ??
          globalFxRate ??
          undefined,
      ]),
    ),
    assetId,
  };
}

function computeRevaluationTotals(
  formData: rules.RevaluationFormType,
  vehicle: Vehicle,
) {
  const allRevaluations = [
    ...(formData.revaluations ?? []),
    ...(formData.newRevaluations ?? []).filter((r) => !r.isRemoved),
  ];

  const revaluationHistory = processRevaluationHistory(allRevaluations);

  const revaluations = filterChangedRevaluations(
    revaluationHistory,
    vehicle.baseCurrency,
    formData.revaluationCurrencyRates,
  );

  const priorHoldValueTotal = sumBy(revaluations, 'priorValuation');
  const newHoldValueTotal = sumBy(revaluations, 'newValuation');
  const totalUnits = sumBy(revaluations, 'currentNumberOfUnits');

  return {
    hasChanges: revaluations.length > 0,
    priorHoldValueTotal,
    newHoldValueTotal,
    priorUnitPriceTotal: totalUnits ? priorHoldValueTotal / totalUnits : 0,
    newUnitPriceTotal: totalUnits ? newHoldValueTotal / totalUnits : 0,
    valuationChangeTotal: sumBy(revaluations, 'valuationChange'),
  };
}

const nameColumns: Array<TableColumn<CompanyRow>> = [
  {
    label: 'Company name',
    key: 'name',
    render: (company) => (
      <Row centered spacing="xs">
        <Avatar
          src={company.companyLogo ?? undefined}
          text={company.name}
          size="lg"
          color="random"
        />
        <div>{company.name}</div>
      </Row>
    ),
  },
];

const toggleExpanded = (
  id: string,
  setter: React.Dispatch<React.SetStateAction<Set<string>>>,
) => {
  setter((prev) =>
    prev.has(id)
      ? new Set([...prev].filter((x) => x !== id))
      : new Set([...prev, id]),
  );
};

const BulkReviewRevaluations: React.FC<Props> = ({
  collectedData,
  vehicle,
  periodId,
  onBack,
  onExit,
}) => {
  const [reviewData, setReviewData] = useState(collectedData);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [expandedSummaryIds, setExpandedSummaryIds] = useState<Set<string>>(
    () => new Set(),
  );

  const handleMutation = useHandleTrpcMutation();

  const mutationOptions = handleMutation({
    successMessage: 'Revaluations saved successfully.',
    onSuccess: () => {
      onExit();
    },
    onError: (err) => captureException(err),
  });
  const { mutate: createRevaluations, isPending: isCreatingRevaluations } =
    api.accountingPeriod.createRevaluationsForPeriodEnd.useMutation(
      mutationOptions,
    );
  const [expandedTransactionIds, setExpandedTransactionIds] = useState<
    Set<string>
  >(() => new Set(Object.keys(collectedData)));

  const handleSave = () => {
    if (!periodId) return;

    const items = values(reviewData)
      .filter((entry) => {
        const { hasChanges } = computeRevaluationTotals(
          entry.formData,
          vehicle,
        );
        return hasChanges;
      })
      .map((entry) => buildItemPayload(entry, vehicle.baseCurrency));

    if (isEmpty(items)) return;

    createRevaluations({ vehicleId: vehicle._id, periodId, items });
  };

  const handleDataChange = (
    companyId: string,
    formData: rules.RevaluationFormType,
  ) => {
    setReviewData((prev) => ({
      ...prev,
      [companyId]: { ...prev[companyId], formData },
    }));
  };

  const companyRows: Array<CompanyRow> = Object.entries(reviewData)
    .map(([companyId, entry]) => ({
      id: companyId,
      name: entry.name,
      assetName: entry.assetName,
      assetId: entry.assetId,
      formData: entry.formData,
      companyLogo: entry.companyLogo,
      assetCurrency: entry.formData.assetCurrency,
      ...computeRevaluationTotals(entry.formData, vehicle),
    }))
    .filter((row) => row.hasChanges);

  const summaryColumns: Array<TableColumn<CompanyRow>> = [
    {
      label: 'Asset',
      key: 'name',
      render: (company) => (
        <Row centered spacing="xs">
          <Avatar
            src={company.companyLogo ?? undefined}
            text={company.name}
            size="lg"
            color="random"
          />
          <div>{company.name}</div>
        </Row>
      ),
    },
    {
      label: 'Prior total hold value',
      key: 'priorHoldValueTotal',
      render: (company) =>
        numberToCurrencyString(company.priorHoldValueTotal, {
          currency: company.assetCurrency,
        }),
    },
    {
      label: 'New total hold value',
      key: 'newHoldValueTotal',
      render: (company) =>
        numberToCurrencyString(company.newHoldValueTotal, {
          currency: company.assetCurrency,
        }),
    },
    {
      label: 'Prior average unit price',
      key: 'priorUnitPriceTotal',
      render: (company) =>
        numberToCurrencyString(company.priorUnitPriceTotal, {
          currency: company.assetCurrency,
        }),
    },
    {
      label: 'New average unit price',
      key: 'newUnitPriceTotal',
      render: (company) =>
        numberToCurrencyString(company.newUnitPriceTotal, {
          currency: company.assetCurrency,
        }),
    },
    {
      label: 'Valuation change',
      key: 'valuationChangeTotal',
      render: (company) =>
        numberToCurrencyString(company.valuationChangeTotal, {
          currency: company.assetCurrency,
        }),
    },
  ];

  return (
    <FullPageDrawer
      open
      size="lg"
      innerScroll
      onClose={onExit}
      title={
        <BulkRevaluationsDrawerTitle
          onClose={onExit}
          activeStep={1}
          title="Review revaluation transactions"
        />
      }
      actions={
        <Row fullWidth justify="space-between">
          <Row spacing="md">
            <Button
              size="large"
              variant="outlined"
              color="primary"
              onClick={onBack}
            >
              Back
            </Button>
          </Row>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={() => setIsConfirmModalOpen(true)}
            loading={isCreatingRevaluations}
          >
            Approve and create transactions
          </Button>
        </Row>
      }
    >
      {isCreatingRevaluations ? (
        <ProcessingPage
          title="Processing revaluations"
          subtitle="This will only take a moment."
        />
      ) : (
        <Column spacing="lg" fullWidth>
          <Typography variant="h5">Revaluation information</Typography>
          <StaticField label="Vehicle">{vehicle.vehicleName}</StaticField>
          <Divider />
          <Column spacing="sm">
            <Typography variant="h5">Review revaluation</Typography>
            <Table
              columns={summaryColumns}
              rows={companyRows}
              rowsTotalCount={companyRows.length}
              isExpandableRow={() => true}
              isExpandedRow={(row) => expandedSummaryIds.has(row.id)}
              onRowExpand={(row) =>
                toggleExpanded(row.id, setExpandedSummaryIds)
              }
              getExpandedContent={(row) => (
                <CompanyRevaluationHistorySection
                  savedData={row.formData}
                  vehicle={vehicle}
                />
              )}
              disableSorting
            />
          </Column>
          <Divider />
          <Column spacing="sm">
            <Typography variant="h5">Review transactions</Typography>
            <Table
              columns={nameColumns}
              hideHeader
              rows={companyRows}
              rowsTotalCount={companyRows.length}
              isExpandableRow={() => true}
              isExpandedRow={(row) => expandedTransactionIds.has(row.id)}
              onRowExpand={(row) =>
                toggleExpanded(row.id, setExpandedTransactionIds)
              }
              getExpandedContent={(row) => (
                <CompanyReviewSection
                  companyId={row.id}
                  assetId={row.assetId}
                  vehicle={vehicle}
                  savedData={row.formData}
                  onDataChange={handleDataChange}
                />
              )}
              disableSorting
            />
          </Column>
        </Column>
      )}
      <ConfirmDialog
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="Approve and create transactions?"
        content={`This will post ${companyRows.length} ${pluralize(
          'transaction set',
          companyRows.length,
        )} to the GL.`}
        actions={{
          confirm: {
            onClick: () => {
              setIsConfirmModalOpen(false);
              handleSave();
            },
            label: 'Approve and create transactions',
          },
        }}
      />
    </FullPageDrawer>
  );
};

export default BulkReviewRevaluations;
